import { useEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';
import { angularInjectorProvider } from 'Injector';
import { type SafeApply } from 'SafeApply';
import {
    type ReactRouterBridge,
    type ReactRouterBridgeRouteChangeStartEvent,
} from 'Navigation/angularModule/scripts/react_router_bridge';

type LocationLike = {
    pathname?: string;
    search?: string;
    hash?: string;
};

/*
    useAllowBlockNavigation fires an event via the reactRouterBridge whenever react-router
    detects a navigation attempt. If anyone calls `preventDefault` on that event, the navigation
    will be aborted.
*/
export function useAllowBlockNavigation(currentLocation: LocationLike): void {
    const lastNavigationWasBlockedRef = useRef(false);
    const previousLocationRef = usePreviousLocationRef(currentLocation);

    const blocker = useBlocker(({ currentLocation: blockerCurrentLocation, nextLocation }) => {
        lastNavigationWasBlockedRef.current = false;

        const injector = angularInjectorProvider.injector;
        if (!injector) {
            return false;
        }

        const normalizedCurrentLocation = normalizeLocationLike(blockerCurrentLocation);
        const previousLocation = hasLocationValues(normalizedCurrentLocation)
            ? normalizedCurrentLocation
            : previousLocationRef.current;
        const normalizedNextLocation = normalizeLocationLike(nextLocation);

        const event = emitRouteChangeStartEvent({
            injector,
            nextLocation: normalizedNextLocation,
            previousLocation,
        });

        lastNavigationWasBlockedRef.current = event.defaultPrevented;
        return event.defaultPrevented;
    });

    useEffect(() => {
        if (blocker.state !== 'blocked') return;

        if (lastNavigationWasBlockedRef.current) {
            blocker.reset();
        } else {
            blocker.proceed();
        }

        lastNavigationWasBlockedRef.current = false;
    }, [blocker, blocker.state]);
}

function emitRouteChangeStartEvent({
    injector,
    nextLocation,
    previousLocation,
}: {
    injector: angular.auto.IInjectorService;
    nextLocation: LocationLike | null;
    previousLocation: LocationLike;
}): ReactRouterBridgeRouteChangeStartEvent {
    const bridge = injector.get<ReactRouterBridge>('reactRouterBridge');
    const $rootScope = injector.get<angular.IRootScopeService>('$rootScope');
    const safeApply = injector.get<SafeApply>('safeApply');

    const event: ReactRouterBridgeRouteChangeStartEvent = {
        nextUrl: buildUrlFromLocation(nextLocation),
        currentUrl: buildUrlFromLocation(previousLocation),
        defaultPrevented: false,
        preventDefault() {
            this.defaultPrevented = true;
        },
    };

    bridge.emit('routeChangeStart', event);
    safeApply($rootScope);
    return event;
}

function usePreviousLocationRef(currentLocation: LocationLike): React.MutableRefObject<LocationLike> {
    const previousLocationRef = useRef(currentLocation);

    useEffect(() => {
        previousLocationRef.current = currentLocation;
    }, [currentLocation]);

    return previousLocationRef;
}

function buildUrlFromLocation(location: LocationLike | null): string {
    if (!location) {
        return '';
    }

    const { pathname = '', search = '', hash = '' } = location;
    if (!pathname && !search && !hash) {
        return '';
    }
    return `${pathname}${search}${hash}`;
}

function normalizeLocationLike(location: Partial<LocationLike> | null | undefined): LocationLike {
    if (!location) {
        return {
            pathname: '',
            search: '',
            hash: '',
        };
    }

    const { pathname = '', search = '', hash = '' } = location;
    return { pathname, search, hash };
}

function hasLocationValues(location: LocationLike): boolean {
    return Boolean(location.pathname || location.search || location.hash);
}
